import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import { prefersReducedMotion } from '@/lib/lenis';
import { ProductCardSkeleton } from '@/components/ui/Skeleton';
import { ProductCard } from './ProductCard';
import type { Product } from '@/types/product';
import { cn } from '@/lib/cn';

/** Number of cards above the fold that get priority image loading.
 *  Matches the 4-column desktop grid layout. */
const PRIORITY_COUNT = 4;

interface ProductGridProps {
  products: Product[];
  isLoading?: boolean;
  skeletonCount?: number;
  onQuickView?: (slug: string) => void;
  emptyMessage?: string;
  className?: string;
}

export function ProductGridSkeletonInline({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function ProductGrid({
  products,
  isLoading = false,
  skeletonCount = 8,
  onQuickView,
  emptyMessage = 'No products found.',
  className,
}: ProductGridProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Number of cards already on screen at the end of the previous run. Lets us
  // animate ONLY the freshly appended cards on "Load More" instead of
  // reverting + re-hiding the whole grid (which caused the blank flash).
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (isLoading || prefersReducedMotion()) {
      // Keep the counter in sync even when we skip animating, so the next real
      // run knows exactly which cards are new.
      prevCountRef.current = products.length;
      return;
    }
    const root = ref.current;
    if (!root) return;

    const cards = root.querySelectorAll<HTMLElement>('.product-card');
    const start = prevCountRef.current;

    const ctx = gsap.context(() => {
      if (start === 0) {
        // First paint: scroll-reveal the whole grid in batches as it enters.
        ScrollTrigger.batch('.product-card', {
          interval: 0.08,
          start: 'top 88%',
          onEnter: (batch) => {
            gsap.from(batch, {
              y: 60,
              opacity: 0,
              duration: 0.6,
              ease: 'power2.out',
              stagger: 0.06,
              overwrite: 'auto',
            });
          },
        });
      } else {
        // Append (Load More): the new cards render right at the viewport where
        // the button was, so animate them in immediately. Already-revealed
        // cards are untouched — no revert, no re-hide, no flash.
        const fresh = Array.from(cards).slice(start);
        gsap.from(fresh, {
          y: 40,
          opacity: 0,
          duration: 0.5,
          ease: 'power2.out',
          stagger: 0.05,
          overwrite: 'auto',
        });
      }
    }, root);

    prevCountRef.current = products.length;
    return () => ctx.revert();
  }, [isLoading, products.length]);

  if (isLoading) {
    return <ProductGridSkeletonInline count={skeletonCount} />;
  }

  if (products.length === 0) {
    return (
      <div
        className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-white/10 p-10 text-center"
        role="status"
      >
        <p className="text-[color:var(--color-text-muted)]">{emptyMessage}</p>
      </div>
    );
  }

  // GSAP ScrollTrigger.batch already handles the staggered reveal — wrapping
  // every card in `<motion.div layout>` with AnimatePresence was redundant and
  // measurably more expensive (layout measurements per child on each render).
  return (
    <div
      ref={ref}
      className={cn(
        'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        className
      )}
    >
      {products.map((product, i) => (
        <ProductCard
          key={product.id}
          product={product}
          index={i}
          priority={i < PRIORITY_COUNT}
          onQuickView={onQuickView}
        />
      ))}
    </div>
  );
}

export { ProductGridSkeletonInline as ProductGridSkeleton };
